/**
 * Uscite di rifiuti prodotti dall'impianto (V71).
 *
 * Tipi speculari a `backend/app/models/uscite_schemas.py`. Due note che il
 * contratto non mostra da solo:
 *
 *  - L'UNITÀ È IL BIG BAG. La creazione manda `big_bag_ids`, mai un peso: il
 *    peso dell'uscita è la somma dei sacchi, e il ledger scala ogni area per i
 *    kg che quell'area ha davvero dato.
 *  - La COMPOSIZIONE È IMMUTABILE. `UscitaUpdate` non ha `big_bag_ids` e non
 *    deve averlo: chi ha sbagliato la composizione annulla e ricrea.
 *
 * I pesi sono `number` perché il backend li espone con `KgDecimal`, che
 * serializza a NUMERO. `fmtKg` coercizza comunque: se un domani un campo
 * tornasse `Decimal` nudo arriverebbe come stringa.
 */
import apiClient from './client';

export type TipoUscita = 'RP' | 'EOW';
export type TipoOperazione = 'vendita' | 'conferimento_a_terzi';
/** Derivato dal backend a ogni lettura, mai persistito. */
export type StatoUscita = 'registrata' | 'fatturata' | 'pagata' | 'annullata';
export type FiltroEcomatico = 'tutti' | 'da_registrare' | 'registrati';

export interface UscitaListItem {
  id: string;
  codice: string;
  tipo: TipoUscita;
  tipo_operazione: TipoOperazione;
  cer_rifiuto_id: string;
  cer_code: string;
  descrizione_rifiuto: string;
  peso_kg: number;
  peso_ponte_kg: number | null;
  numero_fir: string;
  data_partenza: string;
  customer_id: string;
  customer_ragione_sociale: string | null;
  trasportatore_id: string | null;
  trasportatore_ragione_sociale: string | null;
  stato: StatoUscita;
  /**
   * V74 — tipologie DISTINTE dei sacchi caricati. Un'uscita aggrega N big bag
   * che possono avere definizioni diverse dello stesso CER: «19 12 03» non dice
   * se sul camion c'e' alluminio FV o polvere. I sacchi senza tipologia non
   * aggiungono una voce vuota.
   */
  tipologie: string[];
  /**
   * V72 — fattura di vendita che ha assorbito l'uscita. Lo `stato` dice gia'
   * FATTURATA, questo dice DOVE. Null su `conferimento_a_terzi`, che va sulla
   * passiva (fetta 3).
   */
  fattura_id: string | null;
  /**
   * Fetta 3 - fattura PASSIVA su cui l'uscita e' stata registrata. Speculare a
   * `fattura_id` e mai valorizzata insieme a lui (CHECK di V71): il canale lo
   * decide `tipo_operazione`. Null su `vendita`.
   */
  fattura_passiva_id: string | null;
  num_big_bags: number;
  registrato_ecomatico: boolean;
  registrato_ecomatico_at: string | null;
  registrato_ecomatico_da_nome: string | null;
  created_at: string;
}

export interface UscitaListResponse {
  items: UscitaListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface BigBagUscita {
  id: string;
  qr_token: string;
  peso_kg: number;
  data_produzione: string | null;
  produzione_id: string;
  area_id: string;
  area_codice: string | null;
  /** Definizione commerciale del sacco (V74); null sui sacchi anteriori. */
  tipologia_nome?: string | null;
}

/** Riga di ledger con `riferimento_id` = questa uscita. Sono N, una per area. */
export interface MovimentoLedgerUscita {
  area_id: string;
  area_codice: string | null;
  materiale: string;
  delta_kg: number;
  causale: string;
  created_at: string;
}

export interface UscitaDetail extends UscitaListItem {
  note: string | null;
  created_by: string | null;
  creato_da_nome: string | null;
  deleted_at: string | null;
  big_bags: BigBagUscita[];
  movimenti: MovimentoLedgerUscita[];
}

export interface BigBagDisponibile {
  id: string;
  qr_token: string;
  peso_kg: number;
  data_produzione: string | null;
  produzione_id: string;
  area_id: string;
  area_codice: string | null;
  materiale_nome: string | null;
  /** Definizione commerciale del sacco (V74); null sui sacchi anteriori. */
  tipologia_nome?: string | null;
}

export interface BigBagByToken {
  id: string;
  qr_token: string;
  peso_kg: number;
  data_produzione: string | null;
  produzione_id: string;
  produzione_created_at: string;
  area_id: string;
  area_codice: string | null;
  materiale: string;
  materiale_nome: string | null;
  uscita_id: string | null;
  uscita_codice: string | null;
  uscita_data_partenza: string | null;
}

export interface UscitaCreate {
  tipo: TipoUscita;
  tipo_operazione: TipoOperazione;
  cer_rifiuto_id: string;
  big_bag_ids: string[];
  numero_fir: string;
  data_partenza: string;
  customer_id: string;
  trasportatore_id?: string | null;
  peso_ponte_kg?: number | null;
  note?: string | null;
}

/**
 * PATCH parziale. Il backend usa `exclude_unset`: chiave ASSENTE = campo
 * invariato, chiave a `null` = cancellazione esplicita. Nessun `big_bag_ids`,
 * e non è un'omissione (vedi l'intestazione del file).
 */
export interface UscitaUpdate {
  numero_fir?: string;
  data_partenza?: string;
  customer_id?: string;
  trasportatore_id?: string | null;
  peso_ponte_kg?: number | null;
  note?: string | null;
}

export interface UscitaDeleteResponse {
  id: string;
  big_bags_liberati: number;
  movimenti_compensazione: number;
}

export interface UscitaFiltri {
  data_da?: string;
  data_a?: string;
  customer_id?: string;
  cer_rifiuto_id?: string;
  tipo_operazione?: TipoOperazione;
  stato?: StatoUscita;
  ecomatico?: FiltroEcomatico;
  limit?: number;
  offset?: number;
}

export async function listUscite(filtri: UscitaFiltri = {}): Promise<UscitaListResponse> {
  const { data } = await apiClient.get<UscitaListResponse>('/api/uscite', { params: filtri });
  return data;
}

export async function getUscita(id: string): Promise<UscitaDetail> {
  const { data } = await apiClient.get<UscitaDetail>(`/api/uscite/${id}`);
  return data;
}

export async function getBigBagDisponibili(cerRifiutoId: string): Promise<BigBagDisponibile[]> {
  const { data } = await apiClient.get<BigBagDisponibile[]>('/api/uscite/big-bag-disponibili', {
    params: { cer_rifiuto_id: cerRifiutoId },
  });
  return data;
}

export async function createUscita(body: UscitaCreate): Promise<UscitaDetail> {
  const { data } = await apiClient.post<UscitaDetail>('/api/uscite', body);
  return data;
}

export async function updateUscita(id: string, body: UscitaUpdate): Promise<UscitaDetail> {
  const { data } = await apiClient.patch<UscitaDetail>(`/api/uscite/${id}`, body);
  return data;
}

export async function annullaUscita(id: string): Promise<UscitaDeleteResponse> {
  const { data } = await apiClient.delete<UscitaDeleteResponse>(`/api/uscite/${id}`);
  return data;
}

/** Toggle reversibile, come su scarichi e produzioni. */
export async function setEcomaticoUscita(id: string, registrato: boolean): Promise<UscitaDetail> {
  const { data } = await apiClient.patch<UscitaDetail>(`/api/uscite/${id}/ecomatico`, { registrato });
  return data;
}

/** Risolve il token stampato sull'etichetta. 404 se il token non esiste. */
export async function getBigBagByToken(qrToken: string): Promise<BigBagByToken> {
  const { data } = await apiClient.get<BigBagByToken>(`/api/big-bags/by-token/${qrToken}`);
  return data;
}
